import { useCallback, useEffect, useRef, useState } from 'react';
import { detailUrl, thumbUrl } from '@auctisync/shared';

interface Photo {
  id: string;
  display_url: string;
  caption: string | null;
}

interface SwipePhotoCarouselProps {
  photos: Photo[];
  alt: string;
  /** Optional badge rendered over the top-left of the main image (e.g. "No reserve"). */
  badge?: React.ReactNode;
}

const SWIPE_THRESHOLD_PX = 50;

/**
 * The lot gallery — one large frame and a thumbnail strip beneath it.
 *
 * REBUILT to the web design handoff (`web/web-lot.jsx`, 2026-09-02): the frame
 * is `.ph .w-gal-main` at 4:3 and the strip is `.w-gal-strip`, so the gallery
 * sits in the lot grid's first row beside the sticky bid panel. What it
 * replaced drew its own borders and radii in inline styles and therefore
 * ignored the tenant's radius scale entirely.
 *
 * THE GESTURE LAYER IS UNCHANGED. Touch + mouse drag to move between photos,
 * click a thumbnail to jump, ←/→ when the frame has focus. Mobile web is a
 * real surface for this page, and a phone browsing a lot expects to swipe.
 *
 * A LOT WITH NO PHOTOS STILL RENDERS THE FRAME. The empty well occupies the
 * same 4:3 box a photograph would, so the page does not reflow around the
 * bidder as images arrive — and a photoless lot looks unfinished rather than
 * broken, which is the truth of it.
 */
export function SwipePhotoCarousel({ photos, alt, badge }: SwipePhotoCarouselProps) {
  const [index, setIndex] = useState(0);
  const dragStartXRef = useRef<number | null>(null);

  // Clamp index if the photos array shrinks (e.g., one is removed by an admin).
  useEffect(() => {
    if (index >= photos.length && photos.length > 0) {
      setIndex(photos.length - 1);
    }
  }, [photos.length, index]);

  const goTo = useCallback(
    (next: number) => {
      if (photos.length === 0) return;
      const clamped = Math.max(0, Math.min(photos.length - 1, next));
      setIndex(clamped);
    },
    [photos.length],
  );

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    dragStartXRef.current = e.clientX;
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    const start = dragStartXRef.current;
    dragStartXRef.current = null;
    e.currentTarget.releasePointerCapture(e.pointerId);
    if (start == null) return;
    const delta = e.clientX - start;
    if (Math.abs(delta) < SWIPE_THRESHOLD_PX) return;
    if (delta < 0) {
      goTo(index + 1);
    } else {
      goTo(index - 1);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      goTo(index + 1);
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      goTo(index - 1);
    }
  };

  const current = photos[index];

  return (
    <div className="w-gal">
      <div
        className="ph w-gal-main"
        role={photos.length > 0 ? 'region' : undefined}
        aria-label={photos.length > 0 ? `Photo ${index + 1} of ${photos.length}` : undefined}
        tabIndex={photos.length > 1 ? 0 : undefined}
        onPointerDown={photos.length > 1 ? onPointerDown : undefined}
        onPointerUp={photos.length > 1 ? onPointerUp : undefined}
        onKeyDown={photos.length > 1 ? onKeyDown : undefined}
        style={{
          touchAction: 'pan-y',
          cursor: photos.length > 1 ? 'grab' : 'default',
          outline: 'none',
        }}
      >
        {badge ? <span className="w-badge">{badge}</span> : null}
        {current ? (
          <img
            src={detailUrl(current.display_url)}
            alt={current.caption ?? alt}
            draggable={false}
            style={{ userSelect: 'none', pointerEvents: 'none' }}
          />
        ) : (
          <em>No photographs of this lot</em>
        )}
        {photos.length > 1 && (
          <>
            <PagerButton direction="prev" onClick={() => goTo(index - 1)} disabled={index === 0} />
            <PagerButton
              direction="next"
              onClick={() => goTo(index + 1)}
              disabled={index === photos.length - 1}
            />
          </>
        )}
      </div>

      {photos.length > 1 && (
        <div className="w-gal-strip">
          {photos.map((p, i) => (
            <button
              key={p.id}
              type="button"
              className="ph"
              onClick={() => goTo(i)}
              aria-label={`Show photo ${String(i + 1)}`}
              aria-current={i === index}
            >
              <img src={thumbUrl(p.display_url)} alt="" loading="lazy" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function PagerButton({
  direction,
  onClick,
  disabled,
}: {
  direction: 'prev' | 'next';
  onClick: () => void;
  disabled: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={direction === 'prev' ? 'Previous photo' : 'Next photo'}
      style={{
        position: 'absolute',
        top: '50%',
        [direction === 'prev' ? 'left' : 'right']: '10px',
        transform: 'translateY(-50%)',
        width: '38px',
        height: '38px',
        borderRadius: '999px',
        background: 'rgba(0, 0, 0, 0.42)',
        color: 'white',
        fontSize: '1.2rem',
        lineHeight: 1,
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.25 : 1,
        display: 'grid',
        placeItems: 'center',
        zIndex: 2,
      }}
    >
      {direction === 'prev' ? '‹' : '›'}
    </button>
  );
}
